/**
 * Cloudinary URL helpers — improve quality + bandwidth without making the site heavy.
 *
 * Strategy:
 *   - We store the RAW upload URL in DB (untouched, original quality preserved server-side).
 *   - On DISPLAY we inject Cloudinary transformations to get optimal quality + size:
 *       • f_auto       → serve WebP/AVIF when the browser supports it (~50% smaller than JPEG)
 *       • q_auto:good  → smart quality for thumbnails (balanced)
 *       • q_auto:best  → smart quality for full-size views (max quality, still bandwidth-optimized)
 *   - Transformations are cached on Cloudinary's CDN — first hit slightly slower, all subsequent
 *     hits served from cache, fast everywhere.
 */

const TRANSFORM_MARKER = '/image/upload/';
const VIDEO_TRANSFORM_MARKER = '/video/upload/';

/**
 * Inject a transformation string into a Cloudinary URL.
 * If the URL already has transformations after /upload/, they are preserved.
 * If the URL is not a Cloudinary URL, returns it unchanged.
 */
export function cldUrl(url, transform) {
  if (!url || typeof url !== 'string') return url;
  if (!transform) return url;
  let marker = null;
  if (url.includes(TRANSFORM_MARKER)) marker = TRANSFORM_MARKER;
  else if (url.includes(VIDEO_TRANSFORM_MARKER)) marker = VIDEO_TRANSFORM_MARKER;
  if (!marker) return url; // not a Cloudinary URL — leave alone
  const [prefix, rest] = url.split(marker);
  // If there's already a transform segment (looks like "c_xxx,..." then "/v123/..." or "/public_id")
  // we keep it untouched (caller already optimized).
  // Heuristic: a transform segment never starts with "v\d+" and never starts with the public-id.
  const firstSeg = (rest || '').split('/')[0] || '';
  const looksLikeVersion = /^v\d+$/.test(firstSeg);
  const looksLikeTransform =
    !looksLikeVersion &&
    /^[a-z]_/.test(firstSeg) && // transform keys are like w_, h_, c_, q_, f_, e_, dl_, fl_
    firstSeg.includes('_');
  if (looksLikeTransform) return url; // already optimized
  return `${prefix}${marker}${transform}/${rest}`;
}

/** 400×400 thumbnail — fast, used in grids. */
export function cldThumb(url, size = 400) {
  return cldUrl(url, `c_fill,g_auto,w_${size},h_${size},q_auto:good,f_auto`);
}

/** Larger thumbnail (eg 800×800) for hover/preview. */
export function cldMedium(url, size = 800) {
  return cldUrl(url, `c_limit,w_${size},h_${size},q_auto:good,f_auto`);
}

/** Full-size, best quality, format auto — used in lightbox / public viewer. */
export function cldFull(url) {
  return cldUrl(url, 'q_auto:best,f_auto');
}

/** Determine if a URL is a Cloudinary video. */
export function isCloudinaryVideo(url) {
  return typeof url === 'string' && url.includes(VIDEO_TRANSFORM_MARKER);
}

/** Video poster (still frame) — generated on the fly by Cloudinary. */
export function cldVideoPoster(url) {
  if (!isCloudinaryVideo(url)) return url;
  // Replace /video/upload/ with /video/upload/{transform}/ + change extension to .jpg
  const transformed = cldUrl(url, 'so_2,w_800,q_auto,f_jpg');
  return transformed.replace(/\.(mp4|mov|webm|avi)(\?|$)/i, '.jpg$2');
}
